import { useRef, useState } from 'react';
import type {
  AffiliateStockListing,
  CapiProductPage,
  Raffle
} from 'types/capi/pdp';

import CompareRetailersClickEvent from '~/events/CompareRetailersClickEvent';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { getSortedStockListingsForSize } from '~/utils/listings';
import type { Region } from '~/utils/region';

import RaffleListing from '../RaffleListing';
import NotificationCta from './NotificationCta';
import StickyCta from './StickyCta';
import StockListing from './StockListing';
import StockListingGated from './StockListingGated';
import Title from './Title';
import ViewToggle from './ViewToggle';

export enum SelectedView {
  RAFFLES,
  RETAILERS,
  MINIMISED
}

export default function WhereToBuyComingSoon() {
  const { isBot } = useRootData();
  const product = usePageData<CapiProductPage>();
  const { raffle } = useQsParams();
  const region = useRegion();

  const defaultView = getDefaultSelectedView(product, !!raffle, region);

  const [selectedView, setSelectedView] = useState(defaultView);

  const stockListings = getSortedStockListingsForSize(product);
  const isNotified = useUser().isNotificationSetForProduct(product.product_id);

  const hasRaffles = product.stock_listings.raffles.length > 0;

  function onViewToggleClick(newView: SelectedView) {
    setSelectedView(newView);

    if (newView === SelectedView.RETAILERS) {
      return new CompareRetailersClickEvent({ isBot }).send();
    }
  }
  const notificationCtaRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="flex w-full flex-col gap-6 md:col-span-full lg:col-span-4">
        <div className="flex w-full flex-col gap-5">
          <Title />
          <div className="flex w-full flex-col items-center gap-4 rounded-lg bg-grey-panel p-4">
            {product.sub_type === 'sneaker' && region.code === 'GB' && (
              <NotificationCta
                ref={notificationCtaRef}
                product={product}
                isNotified={isNotified}
              />
            )}

            {(hasRaffles || selectedView === SelectedView.MINIMISED) && (
              <ViewToggle
                stockListings={product.stock_listings}
                selectedView={selectedView}
                setSelectedView={onViewToggleClick}
              />
            )}

            {selectedView === SelectedView.RETAILERS && (
              <StockListings listings={stockListings} />
            )}

            {selectedView === SelectedView.RAFFLES && (
              <Raffles raffles={product.stock_listings.raffles} />
            )}
          </div>
        </div>
      </div>
      <StickyCta
        showWhileHiddenRef={notificationCtaRef}
        stockListing={stockListings[0]!}
      />
    </>
  );
}

const StockListings = ({
  listings,
  selectedSize
}: {
  listings: AffiliateStockListing[];
  selectedSize?: string;
}) => {
  const user = useUser();
  const region = useRegion();

  return (
    <>
      {listings.map((listing, index) =>
        region.code !== 'GB' && !user.isLoggedIn ? (
          <StockListingGated
            key={listing.affiliate.name}
            pagePosition="WTB"
            listing={listing}
            selectedSize={selectedSize}
          />
        ) : (
          <StockListing
            key={listing.affiliate.name}
            position={index + 1}
            pagePosition="WTB"
            listing={listing}
            selectedSize={selectedSize}
          />
        )
      )}
    </>
  );
};

function Raffles({ raffles }: { raffles: Raffle[] }) {
  return (
    <div className="flex w-full flex-col gap-4">
      {raffles.map((raffle, index) => (
        <div key={raffle.id}>
          <RaffleListing raffleListing={raffle} buttonPosition={index} />
        </div>
      ))}
    </div>
  );
}

function getDefaultSelectedView(
  product: CapiProductPage,
  raffle: boolean,
  region: Region
) {
  const hasRaffles = product.stock_listings.raffles.length > 0;

  if (raffle && hasRaffles) return SelectedView.RAFFLES;

  if (region.code !== 'GB') {
    return SelectedView.RETAILERS;
  }

  const hasMarketplace = product.stock_listings.affiliates.some(
    (a) => a.affiliate.id === 'affiliate:marketplace'
  );

  return hasMarketplace ? SelectedView.RETAILERS : SelectedView.MINIMISED;
}
